"use client";

/**
 * Shared body of the enrollment-preview surface.
 *
 * This is the orchestration + layout that both the (now-defunct) full-page
 * `Preview` route and the primary `EnrollmentPreviewDrawer` render, so the
 * commit / discard / patch handlers, bucket URL state, candidate selection,
 * and 409 error mapping live in exactly one place.
 *
 * Presentation is container-agnostic (`h-full`): the page wraps it in an
 * `h-screen` div; the drawer drops it straight into a `Sheet` popup (which
 * is already `flex h-full flex-col`).
 *
 * Navigation is injected as callbacks because a drawer has no route to go
 * to:
 *   - `onClose()`     — dismiss (page: navigate back; drawer: closeDrawer).
 *                       Fired on plain close and after a discard.
 *   - `onCommitted()` — after a successful commit (page: route to the
 *                       enrollments tab; drawer: invalidate caches + close).
 *
 * The parent can also imperatively request a guarded close (used by the
 * drawer's Esc / backdrop dismissal) via the exposed `requestClose()`
 * handle so the has-overrides discard prompt fires for every close path.
 */

import * as React from "react";
import { useRouter, useSearchParams } from "next/navigation";

import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import useIsMobile from "@/hooks/use-is-mobile";
import { getApiErrorMessage } from "@/lib/api/error";
import { flushAllPendingOverrides } from "../../hooks/use-debounced-override";
import { useEnrollmentPreview } from "../../hooks/use-enrollment-preview";
import type { PreviewBucket } from "../../lib/types";
import { usePreviewStore } from "../../store/preview-store";

import { DiscardConfirm } from "./discard-confirm";
import { RecipientList } from "./recipient-list";
import { RemoveCandidateConfirm } from "./remove-candidate-confirm";
import { StepPane } from "./step-pane";

const VALID_BUCKETS: ReadonlySet<PreviewBucket> = new Set([
  "no_issue",
  "warning",
  "fix",
]);

function parseBucket(value: string | null): PreviewBucket {
  if (value && VALID_BUCKETS.has(value as PreviewBucket)) {
    return value as PreviewBucket;
  }
  return "no_issue";
}

/** Best-effort recovery of the standard `code` field from a backend
 * error envelope without taking a hard dep on the global Axios shape.
 * Falls back to `null` when the error doesn't follow our convention. */
function extractErrorCode(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as {
    response?: { data?: { detail?: { code?: unknown } } };
    detail?: { code?: unknown };
  };
  const code = e?.response?.data?.detail?.code ?? e?.detail?.code ?? null;
  return typeof code === "string" ? code : null;
}

export interface PreviewContentHandle {
  /** Run the has-overrides discard guard, then dismiss if clean. */
  requestClose: () => void;
}

export interface PreviewContentProps {
  jobId: string;
  sequenceId: string;
  previewSessionId: string;
  /** Dismiss the surface. Fired on plain close and after a discard. */
  onClose: () => void;
  /** Fired after a successful commit with the enrolled-candidate count. */
  onCommitted: (enrollmentCount: number) => void;
  /** Label for the error-card primary action (default: "Back to sequence"). */
  errorActionLabel?: string;
}

export const PreviewContent = React.forwardRef<
  PreviewContentHandle,
  PreviewContentProps
>(function PreviewContent(
  {
    jobId,
    sequenceId,
    previewSessionId,
    onClose,
    onCommitted,
    errorActionLabel = "Back to sequence",
  },
  ref,
) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // ARBI-677: below `lg` the surface is a two-step flow — recipient list
  // first, then the step pane full-width once a candidate is selected. Pane
  // visibility itself is class-driven (`max-lg:*` off the selection) so a
  // desktop window resized below `lg` behaves without JS; `isMobile` only
  // gates the mobile-only controls (Back in the step pane, close X in the
  // list header), mirroring the inbox two-step flow (ARBI-676).
  const isMobile = useIsMobile();

  const activeBucket = parseBucket(searchParams.get("bucket"));

  // Update the URL search params without losing existing ones (e.g. an
  // upstream flow might thread its own `?from=...` for analytics). Works
  // fine inside a drawer on the same page.
  const setBucket = React.useCallback(
    (next: PreviewBucket) => {
      const url = new URL(window.location.href);
      url.searchParams.set("bucket", next);
      router.replace(`${url.pathname}${url.search}`, { scroll: false });
    },
    [router],
  );

  const {
    get,
    discardPreview,
    discard,
    patchPreview,
    patch,
    commitPreview,
    commit,
  } = useEnrollmentPreview({
    jobId,
    sequenceId,
    previewSessionId,
  });
  const preview = get.data ?? null;

  const selectedJobCandidateId = usePreviewStore(
    (s) => s.selectedJobCandidateId,
  );
  const selectCandidate = usePreviewStore((s) => s.selectCandidate);
  const reset = usePreviewStore((s) => s.reset);

  // Reset the preview UI store on unmount so re-opening a fresh session
  // doesn't leak the prior selection.
  React.useEffect(() => {
    return () => reset();
  }, [reset]);

  // If the bucket switches to one that doesn't contain the currently-
  // selected candidate, drop the selection so the right pane reverts to
  // the empty state (spec §6.4).
  React.useEffect(() => {
    if (!preview || !selectedJobCandidateId) return;
    const stillVisible = preview.candidates.some(
      (c) =>
        c.job_candidate_id === selectedJobCandidateId &&
        (c.bucket ?? "no_issue") === activeBucket,
    );
    if (!stillVisible) selectCandidate(null);
  }, [preview, selectedJobCandidateId, activeBucket, selectCandidate]);

  const selectedCandidate = React.useMemo(() => {
    if (!preview || !selectedJobCandidateId) return null;
    return (
      preview.candidates.find(
        (c) => c.job_candidate_id === selectedJobCandidateId,
      ) ?? null
    );
  }, [preview, selectedJobCandidateId]);

  // Mobile step 2 → step 1: clear the selection so the class-driven panes
  // swap back to the full-width recipient list.
  const handleBackToList = React.useCallback(
    () => selectCandidate(null),
    [selectCandidate],
  );

  const [discardOpen, setDiscardOpen] = React.useState(false);
  const [pendingRemoveId, setPendingRemoveId] = React.useState<string | null>(
    null,
  );

  const pendingRemoveCandidate = React.useMemo(() => {
    if (!preview || !pendingRemoveId) return null;
    return (
      preview.candidates.find((c) => c.job_candidate_id === pendingRemoveId) ??
      null
    );
  }, [preview, pendingRemoveId]);

  const handleRemoveCandidate = React.useCallback(async () => {
    if (!pendingRemoveId) return;
    try {
      await patchPreview({
        remove_job_candidate_ids: [pendingRemoveId],
      });
      // Drop the selection if we just removed the focused row.
      if (selectedJobCandidateId === pendingRemoveId) {
        selectCandidate(null);
      }
    } catch (err) {
      showToast.error(
        "Couldn't remove that candidate. Please try again in a moment.",
        { description: getApiErrorMessage(err) },
      );
    } finally {
      setPendingRemoveId(null);
    }
  }, [pendingRemoveId, patchPreview, selectedJobCandidateId, selectCandidate]);

  const handleClose = React.useCallback(() => {
    const hasOverrides = (preview?.candidates ?? []).some(
      (c) => c.has_overrides,
    );
    if (hasOverrides) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }, [preview, onClose]);

  // Expose the guarded close so the drawer's Esc / backdrop dismissal hits
  // the same has-overrides prompt the top-bar X does.
  React.useImperativeHandle(
    ref,
    () => ({ requestClose: handleClose }),
    [handleClose],
  );

  const handleDiscard = React.useCallback(async () => {
    try {
      await discardPreview();
    } catch (err) {
      // Discard is best-effort — the Redis TTL eventually wipes the
      // session anyway. Surface a soft toast and still dismiss.
      showToast.error(
        "Couldn't discard the preview cleanly. It will expire automatically.",
        { description: getApiErrorMessage(err) },
      );
    } finally {
      setDiscardOpen(false);
      onClose();
    }
  }, [discardPreview, onClose]);

  const totalCount = preview?.candidates.length ?? 0;
  const fixCount = preview?.buckets.fix ?? 0;
  const warningCount = preview?.buckets.warning ?? 0;

  const handleCommit = React.useCallback(async () => {
    if (!preview) return;
    try {
      // Drain every pending debounced override first — an edit typed
      // <800ms before clicking Enroll must be in the frozen content
      // ("what you preview is what sends").
      await flushAllPendingOverrides();
      const result = await commitPreview();
      const enrollmentCount = result?.enrollment_count ?? 0;
      showToast.success(
        `Enrolling ${enrollmentCount} ${
          enrollmentCount === 1 ? "candidate" : "candidates"
        }… first sends are queued.`,
      );
      onCommitted(enrollmentCount);
    } catch (err) {
      // Map known 409 codes from spec §10.6 onto user-facing copy. We
      // sniff the standard FastAPI envelope `{ status_code, detail:
      // { code } }`; missing/unknown codes fall through to the generic
      // toast so the user sees acknowledgement either way.
      const code = extractErrorCode(err);
      if (code === "STEP_SET_CHANGED_DURING_PREVIEW") {
        showToast.error(
          "The sequence changed since you opened this preview. Please re-open it.",
        );
      } else if (
        code === "MAILBOX_DISCONNECTED_DURING_PREVIEW" ||
        code === "SENDER_MAILBOX_NOT_CONNECTED"
      ) {
        showToast.error(
          "Sender mailbox is disconnected. Reconnect it in Settings → Connected Accounts.",
        );
      } else if (code === "SEQUENCE_ARCHIVED_DURING_PREVIEW") {
        showToast.error(
          "This sequence is no longer active. Only active sequences accept new enrollments.",
        );
      } else if (code === "ENROLLMENT_JOB_NOT_ENROLLABLE") {
        showToast.error(
          "This job is no longer enrollable. Pick a different job.",
        );
      } else {
        showToast.error(
          "Couldn't enroll the cohort. Please try again in a moment.",
          { description: getApiErrorMessage(err) },
        );
      }
    }
  }, [commitPreview, preview, onCommitted]);

  // 410-Gone (TTL expiry) and 404 (bad id) both flow through here. We
  // surface a friendly empty card instead of an exception.
  if (get.isError) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-surface-primary p-6">
        <div className="flex max-w-md flex-col items-center gap-4 rounded-lg border border-border bg-surface-elevated p-8 text-center">
          <Icon
            name="IconCircleX"
            size={32}
            className="text-destructive opacity-90"
          />
          <h2 className="text-lg font-semibold text-heading">
            Preview no longer available
          </h2>
          <p className="text-sm text-muted-foreground">
            This preview session has expired or was cancelled. Re-open the
            cohort from the sequence page to start again.
          </p>
          <button
            type="button"
            onClick={onClose}
            className="mt-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            {errorActionLabel}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-surface-primary">
      <RecipientList
        preview={preview}
        isLoading={get.isLoading}
        activeBucket={activeBucket}
        onBucketChange={setBucket}
        selectedJobCandidateId={selectedJobCandidateId}
        onSelectCandidate={selectCandidate}
        onRemoveCandidate={setPendingRemoveId}
        totalCount={totalCount}
        fixCount={fixCount}
        warningCount={warningCount}
        onCommit={handleCommit}
        isCommitting={commit.isPending}
        onClose={isMobile ? handleClose : undefined}
      />
      <StepPane
        totalCount={totalCount}
        selectedCandidate={selectedCandidate}
        jobId={jobId}
        sequenceId={sequenceId}
        pinnedStepSetVersionNumber={
          preview?.pinned_step_set_version_number ?? null
        }
        senderTz={null}
        onClose={handleClose}
        onBack={isMobile && selectedCandidate ? handleBackToList : undefined}
        patchPreview={patchPreview}
      />
      <DiscardConfirm
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        onConfirm={handleDiscard}
        isPending={discard.isPending}
      />
      <RemoveCandidateConfirm
        open={Boolean(pendingRemoveId)}
        onOpenChange={(next) => {
          if (!next) setPendingRemoveId(null);
        }}
        candidateName={pendingRemoveCandidate?.full_name ?? null}
        onConfirm={handleRemoveCandidate}
        isPending={patch.isPending}
      />
    </div>
  );
});
